<template>
  <BaseModal :modalTitle="$t('W: Vis som')">
    <div class="flex flex-col gap-4">
      <UiPillNav
        :navItems="pillItems"
        @nav-item-click="onPillClick" />

      <UiBaseFormVee
        submitOnCtrlEnter
        :errorText="errorText"
        :onSubmit="onSubmit">
        <!-- None — clears any active override -->
        <p
          v-if="activeMode === 'none'"
          class="text-sm text-text-secondary">
          {{ $t('W: Nulstiller til fuld admin-adgang uden nogen tilsidesættelse') }}
        </p>

        <!-- Self — freely pick any role/work-area combination for the admin's own session -->
        <div
          v-if="activeMode === 'self'"
          class="flex flex-col gap-4">
          <p class="text-sm text-text-secondary">
            {{ $t('W: Vælg en rolle og arbejdsområder for din egen forhåndsvisning') }}
          </p>
          <RoleSelection v-model:userMainRole="selfMainRole" />
          <WorkAreaSelection v-model:userWorkAreas="selfWorkAreas" />
        </div>

        <!-- User — view the app as a real employee of the current lab -->
        <div
          v-if="activeMode === 'user'"
          class="flex flex-col gap-4">
          <p class="text-sm text-text-secondary">
            {{ $t('W: Vælg en medarbejder — deres reelle rolle og arbejdsområder anvendes') }}
          </p>
          <UiComboboxVee
            name="impersonateUserID"
            :label="$t('W: Medarbejder')"
            :placeholder="$t('W: Vælg medarbejder')"
            :options="employeeOptions"
            v-model="selectedUserID" />
        </div>

        <template #buttons>
          <UiBaseButton
            :text="$t('action.save')"
            :isDisabled="isSubmitDisabled"
            :isLoading="isLoading"
            :showSaveIcon="showSaveIcon"
            type="submit" />
        </template>
      </UiBaseFormVee>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import type { ITabNavOption } from '@team-milva/lab-components'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useUserStore } from '@/stores/userStore'
import { getRoleDisplayName } from '@/globals/typescript/utils/roleUtils'
import { useToast } from '@/globals/typescript/utils/toast'
import type {
  TLabManagementRole,
  TLabManagementWorkArea,
} from '@team-milva/lab-models/user'
import { mapActions, mapState } from 'pinia'

type TImpersonationPillMode = 'none' | 'self' | 'user'

export default {
  name: 'ImpersonationModal',

  emits: ['close'],

  data() {
    const userStore = useUserStore()

    return {
      activeMode: userStore.impersonationMode as TImpersonationPillMode,
      selfMainRole: (userStore.impersonationSelf?.mainRole ??
        'owner') as TLabManagementRole,
      selfWorkAreas: [
        ...(userStore.impersonationSelf?.workAreas ?? []),
      ] as TLabManagementWorkArea[],
      selectedUserID: userStore.impersonationUser?.userID ?? '',
      errorText: '',
      isLoading: false,
      showSaveIcon: false,
    }
  },

  computed: {
    ...mapState(useAccountStore, ['allLabAccountUsers', 'labAccount']),
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),

    pillItems(): ITabNavOption[] {
      return [
        {
          id: 'none',
          title: this.$t('W: Ingen'),
          isActive: this.activeMode === 'none',
        },
        {
          id: 'self',
          title: this.$t('W: Mig selv'),
          isActive: this.activeMode === 'self',
        },
        {
          id: 'user',
          title: this.$t('W: Medarbejder'),
          isActive: this.activeMode === 'user',
        },
      ]
    },

    employeeOptions(): { value: string; text: string; subtitle: string }[] {
      return [...this.allLabAccountUsers]
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
        .map((employee) => {
          const workAreaNames = employee.workAreas.map((workArea) =>
            getRoleDisplayName(workArea)
          )
          return {
            value: employee.id,
            text: employee.fullName,
            subtitle: [getRoleDisplayName(employee.mainRole), ...workAreaNames].join(
              ', '
            ),
          }
        })
    },

    isSubmitDisabled(): boolean {
      return this.activeMode === 'user' && !this.selectedUserID
    },
  },

  methods: {
    ...mapActions(useAccountStore, ['getAllLabAccountUsers']),
    ...mapActions(useUserStore, [
      'setImpersonationNone',
      'setImpersonationSelf',
      'setImpersonationUser',
    ]),

    onPillClick(id: string): void {
      this.activeMode = id as TImpersonationPillMode
      this.errorText = ''
    },

    async onSubmit(): Promise<void> {
      this.errorText = ''

      // activeMode === 'user' needs its target resolved before the
      // isLoading/try boundary below — an unresolved employee is a
      // contextual validation failure, not an unexpected one.
      let employee: (typeof this.allLabAccountUsers)[number] | undefined
      if (this.activeMode === 'user') {
        employee = this.allLabAccountUsers.find(
          (user) => user.id === this.selectedUserID
        )
        if (!employee || !this.labAccount) {
          this.errorText = this.$t('error.common.standard')
          return
        }
      }

      // setImpersonationNone/Self/User are synchronous local Pinia-state
      // writes with no real async boundary — isLoading/try-catch are added
      // anyway to keep this save consistent with every other mutating
      // action's feedback pattern (see Modal UX Guidelines § Loading state).
      this.isLoading = true
      try {
        if (this.activeMode === 'none') {
          this.setImpersonationNone()
          useToast().success(this.$t('W: Stoppede visning som anden bruger'))
        } else if (this.activeMode === 'self') {
          this.setImpersonationSelf(this.selfMainRole, [...this.selfWorkAreas])
          useToast().success(this.$t('W: Forhåndsvisning opdateret'))
        } else if (employee && this.labAccount) {
          this.setImpersonationUser({
            userID: employee.id,
            labAccountID: this.labAccount.id,
            mainRole: employee.mainRole,
            workAreas: [...employee.workAreas],
          })
          useToast().success(
            this.$t('W: Viser nu som {name}', { name: employee.fullName })
          )
        }

        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.$emit('close')
      } catch {
        useToast().error(this.$t('W: Kunne ikke opdatere forhåndsvisning'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // Employees for the current lab — a plain Firestore listener, already
    // the established source for the employee list elsewhere in the app
    // (EmployeesListView.vue). Idempotent, safe to call even if already
    // loaded by another view.
    this.getAllLabAccountUsers()
  },
}
</script>
